import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth';
import { useNavigate } from 'react-router-dom';
import api from '../lib/api';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import AnalyticsModule from '../components/admin/AnalyticsModule';
import CatalogModule from '../components/admin/CatalogModule';
import SurveysModule from '../components/admin/SurveysModule';
import CommunityModule from '../components/admin/CommunityModule';
import CMSModule from '../components/admin/CMSModule';
import NotificationsModule from '../components/admin/NotificationsModule';
import CRMModule from '../components/admin/CRMModule';
import SettingsModule from '../components/admin/SettingsModule';
import {
  BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell
} from 'recharts';
import {
  BarChart3, Users, FileText, Handshake, Building2, MessageSquare,
  Eye, Clock, CheckCircle, TrendingUp,
  LogOut, Home, HelpCircle, Download, Zap, Star, CalendarDays, Plus, X,
  Bell, Settings, BookOpen, Database, ClipboardList, Layout,
  Users2, ChevronDown, ChevronRight, Cpu, BookMarked, Edit, XCircle
} from 'lucide-react';

const STATUS_COLORS = {
  new: 'bg-blue-100 text-blue-700', qualified: 'bg-indigo-100 text-indigo-700',
  in_study: 'bg-yellow-100 text-yellow-700', proposal_sent: 'bg-purple-100 text-purple-700',
  confirmed: 'bg-green-100 text-green-700', coordinating: 'bg-teal-100 text-teal-700',
  completed: 'bg-emerald-100 text-emerald-700', closed: 'bg-gray-100 text-gray-600',
  pending: 'bg-orange-100 text-orange-700', active: 'bg-green-100 text-green-700',
  rejected: 'bg-red-100 text-red-700', contacted: 'bg-cyan-100 text-cyan-700',
  converted: 'bg-emerald-100 text-emerald-700'
};
const STATUS_LABELS = {
  new: 'Nouveau', qualified: 'Qualifié', in_study: "En étude", proposal_sent: 'Proposition envoyée',
  confirmed: 'Confirmé', coordinating: 'Coordination', completed: 'Terminé', closed: 'Clôturé',
  pending: 'En attente', active: 'Actif', rejected: 'Refusé', contacted: 'Contacté', converted: 'Converti'
};
const STAY_STATUSES = ['new', 'qualified', 'in_study', 'proposal_sent', 'confirmed', 'coordinating', 'completed', 'closed'];
const CHART_COLORS = ['#0F394C', '#4A7C59', '#E07A5F', '#6366F1', '#0EA5E9', '#F59E0B', '#10B981', '#6B7280'];
const CAT_LABELS = { hebergement: 'Hébergement', transport_pmr: 'Transport PMR', assistance_humaine: 'Assistance', materiel: 'Matériel', activites: 'Activités' };

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState('overview');
  const [stats, setStats] = useState(null);
  const [stayRequests, setStayRequests] = useState([]);
  const [partners, setPartners] = useState([]);
  const [b2bRequests, setB2bRequests] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [leads, setLeads] = useState([]);
  const [faqItems, setFaqItems] = useState([]);
  const [users, setUsers] = useState([]);
  const [noteText, setNoteText] = useState('');
  const [faqForm, setFaqForm] = useState({ question: '', answer: '', category: 'general', order: 0 });
  const [editingFaq, setEditingFaq] = useState(null);
  // Matching state
  const [matchData, setMatchData] = useState({});
  const [matchLoading, setMatchLoading] = useState({});
  // Booking state
  const [bookings, setBookings] = useState([]);
  const [bookingsReady, setBookingsReady] = useState(null);
  const [showCreateBooking, setShowCreateBooking] = useState(false);
  const [allSlots, setAllSlots] = useState([]);
  const [bookingForm, setBookingForm] = useState({
    partner_id: '', stay_request_id: '', caregiver_name: '',
    partner_name: '', start_date: '', end_date: '', notes: ''
  });

  const loadData = useCallback(async () => {
    const [s, sr, p, b, c, l, f] = await Promise.allSettled([
      api.get('/admin/stats'), api.get('/stay-requests'), api.get('/partners'),
      api.get('/b2b-requests'), api.get('/contact-requests'), api.get('/admin/leads'),
      api.get('/faq/all')
    ]);
    if (s.status === 'fulfilled') setStats(s.value.data);
    if (sr.status === 'fulfilled') setStayRequests(sr.value.data);
    if (p.status === 'fulfilled') setPartners(p.value.data);
    if (b.status === 'fulfilled') setB2bRequests(b.value.data);
    if (c.status === 'fulfilled') setContacts(c.value.data);
    if (l.status === 'fulfilled') setLeads(l.value.data);
    if (f.status === 'fulfilled') setFaqItems(f.value.data);
    if (user?.role === 'super_admin') {
      try { const u = await api.get('/admin/users'); setUsers(u.data); } catch {}
    }
  }, [user]);

  useEffect(() => { loadData(); }, [loadData]);

  const updateStatus = async (type, id, status) => {
    try {
      await api.put(`/${type}/${id}/status`, { status });
      toast.success('Statut mis à jour');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const addNote = async (type, id) => {
    if (!noteText.trim()) return;
    try {
      await api.post(`/${type}/${id}/notes`, { content: noteText });
      setNoteText('');
      toast.success('Note ajoutée');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const saveFaq = async () => {
    try {
      if (editingFaq) {
        await api.put(`/faq/${editingFaq}`, faqForm);
      } else {
        await api.post('/faq', faqForm);
      }
      setFaqForm({ question: '', answer: '', category: 'general', order: 0 });
      setEditingFaq(null);
      toast.success('FAQ sauvegardée');
      loadData();
    } catch { toast.error('Erreur'); }
  };

  const deleteFaq = async (id) => {
    await api.delete(`/faq/${id}`);
    toast.success('FAQ supprimée');
    loadData();
  };

  const loadMatches = async (requestId) => {    if (matchData[requestId]) return;
    setMatchLoading(p => ({ ...p, [requestId]: true }));
    try {
      const res = await api.get(`/stay-requests/${requestId}/matches`);
      setMatchData(p => ({ ...p, [requestId]: res.data }));
    } catch { toast.error('Erreur chargement matching'); }
    finally { setMatchLoading(p => ({ ...p, [requestId]: false })); }
  };

  const loadBookings = useCallback(async () => {
    try {
      const check = await api.get('/bookings/check');
      setBookingsReady(check.data.ready);
      if (check.data.ready) {
        const res = await api.get('/bookings');
        setBookings(res.data);
      }
      const slotsRes = await api.get('/slots');
      setAllSlots(slotsRes.data);
    } catch { setBookingsReady(false); }
  }, []);

  useEffect(() => { if (tab === 'planning') loadBookings(); }, [tab, loadBookings]);

  const createBooking = async (e) => {
    e.preventDefault();
    if (!bookingForm.partner_id || !bookingForm.start_date || !bookingForm.end_date) {
      toast.error('Partenaire et dates sont obligatoires'); return;
    }
    try {
      const selectedPartner = partners.find(p => p.id === bookingForm.partner_id);
      const selectedReq = stayRequests.find(r => r.id === bookingForm.stay_request_id);
      await api.post('/bookings', {
        ...bookingForm,
        partner_name: selectedPartner?.company_name || '',
        caregiver_name: selectedReq ? `${selectedReq.first_name} ${selectedReq.last_name}` : bookingForm.caregiver_name,
        caregiver_user_id: null
      });
      toast.success('Réservation créée avec succès');
      setShowCreateBooking(false);
      setBookingForm({ partner_id: '', stay_request_id: '', caregiver_name: '', partner_name: '', start_date: '', end_date: '', notes: '' });
      loadBookings();
    } catch { toast.error('Erreur lors de la création'); }
  };

  const updateBookingStatus = async (bookingId, newStatus) => {
    try {
      await api.put(`/bookings/${bookingId}/status`, { status: newStatus });
      toast.success('Statut mis à jour');
      loadBookings();
    } catch { toast.error('Erreur'); }
  };

  const assignPartner = async (requestId, partner) => {
    try {
      await api.post(`/stay-requests/${requestId}/assign-partner`, {
        partner_id: partner.id, partner_company: partner.company_name
      });
      toast.success(`${partner.company_name} assigné avec succès`);
      loadData();
    } catch { toast.error('Erreur lors de l\'assignation'); }
  };

  const exportCSV = (data, filename, columns) => {
    if (!data.length) { toast.error('Aucune donnée à exporter'); return; }
    const header = columns.map(c => c.label).join(',');
    const rows = data.map(row =>
      columns.map(c => {
        const val = c.key.split('.').reduce((o, k) => o?.[k], row) ?? '';
        return `"${String(val).replace(/"/g, '""')}"`;
      }).join(',')
    );
    const csv = [header, ...rows].join('\n');
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a'); a.href = url; a.download = filename; a.click();
    URL.revokeObjectURL(url);
    toast.success(`${filename} téléchargé`);
  };

  const CSV_CONFIGS = {
    stays: { filename: 'demandes_sejour.csv', columns: [
      { key: 'last_name', label: 'Nom' }, { key: 'first_name', label: 'Prénom' },
      { key: 'email', label: 'Email' }, { key: 'phone', label: 'Téléphone' },
      { key: 'status', label: 'Statut' }, { key: 'location_preference', label: 'Destination' },
      { key: 'preferred_period', label: 'Période' }, { key: 'mobility_needs', label: 'Mobilité' },
      { key: 'indicative_budget', label: 'Budget' }, { key: 'created_at', label: 'Date' }
    ]},
    partners: { filename: 'partenaires.csv', columns: [
      { key: 'company_name', label: 'Entreprise' }, { key: 'category', label: 'Catégorie' },
      { key: 'contact_email', label: 'Email' }, { key: 'covered_zone', label: 'Zone' },
      { key: 'status', label: 'Statut' }, { key: 'quality_score', label: 'Score' }, { key: 'created_at', label: 'Date' }
    ]},
    b2b: { filename: 'demandes_b2b.csv', columns: [
      { key: 'organization', label: 'Organisation' }, { key: 'actor_type', label: 'Type' },
      { key: 'contact_email', label: 'Email' }, { key: 'needs', label: 'Besoins' },
      { key: 'status', label: 'Statut' }, { key: 'created_at', label: 'Date' }
    ]},
    leads: { filename: 'leads.csv', columns: [
      { key: 'name', label: 'Nom' }, { key: 'email', label: 'Email' },
      { key: 'type', label: 'Type' }, { key: 'source', label: 'Source' },
      { key: 'status', label: 'Statut' }, { key: 'created_at', label: 'Date' }
    ]},
    contacts: { filename: 'messages_contact.csv', columns: [
      { key: 'name', label: 'Nom' }, { key: 'email', label: 'Email' },
      { key: 'subject', label: 'Sujet' }, { key: 'message', label: 'Message' }, { key: 'created_at', label: 'Date' }
    ]}
  };

  // ---- Chart data computed from loaded state ----
  const pipelineData = stats ? STAY_STATUSES.map(s => ({
    name: STATUS_LABELS[s].substring(0, 8), full: STATUS_LABELS[s],
    value: stats.stay_requests.pipeline[s] || 0
  })) : [];

  const partnerCatData = (() => {
    const counts = {};
    partners.forEach(p => { counts[p.category] = (counts[p.category] || 0) + 1; });
    return Object.entries(counts).map(([k, v]) => ({ name: CAT_LABELS[k] || k, value: v }));
  })();

  const leadsStatusData = (() => {
    const counts = {};
    leads.forEach(l => { counts[l.status] = (counts[l.status] || 0) + 1; });
    return Object.entries(counts).map(([k, v]) => ({ name: STATUS_LABELS[k] || k, value: v }));
  })();

  const sideGroups = [
    {
      label: 'TABLEAU DE BORD',
      items: [
        { key: 'overview', icon: BarChart3, label: 'Vue d\'ensemble' },
        { key: 'analytics', icon: TrendingUp, label: 'Analytics' },
      ]
    },
    {
      label: 'OPÉRATIONS',
      items: [
        { key: 'stays', icon: FileText, label: 'Demandes séjour' },
        { key: 'catalog', icon: BookOpen, label: 'Catalogue séjours' },
        { key: 'planning', icon: CalendarDays, label: 'Réservations' },
        { key: 'matching', icon: Zap, label: 'IA Matching' },
      ]
    },
    {
      label: 'RELATIONS',
      items: [
        { key: 'partners', icon: Handshake, label: 'Partenaires' },
        { key: 'b2b', icon: Building2, label: 'Organisations B2B' },
        { key: 'contacts', icon: MessageSquare, label: 'Contacts' },
        { key: 'leads', icon: TrendingUp, label: 'Leads' },
        { key: 'surveys', icon: ClipboardList, label: 'Enquête Aidants' },
      ]
    },
    {
      label: 'CONTENU',
      items: [
        { key: 'community', icon: Users2, label: 'Communauté' },
        { key: 'faq', icon: HelpCircle, label: 'FAQ' },
        { key: 'cms', icon: Layout, label: 'CMS Pages' },
      ]
    },
    {
      label: 'ADMINISTRATION',
      items: [
        { key: 'notifications', icon: Bell, label: 'Notifications' },
        ...(user?.role === 'super_admin' ? [{ key: 'users', icon: Users, label: 'Utilisateurs' }] : []),
      ]
    },
  ];

  const modulesItems = [
    { key: 'crm', icon: Database, label: 'CRM Avancé' },
    { key: 'settings', icon: Settings, label: 'Paramétrage' },
  ];

  // Flat list for mobile select
  const sideItems = sideGroups.flatMap(g => g.items).concat(modulesItems);

  // Shared note component
  const getAssignedPartner = (req) => req.notes?.find(n => n.type === 'partner_match');

  return (
    <div className="min-h-screen bg-background flex" data-testid="admin-dashboard">
      {/* Sidebar */}
      <aside className="hidden md:flex w-64 bg-primary text-primary-foreground flex-col fixed inset-y-0 left-0 z-40">
        {/* Logo */}
        <div className="p-5 border-b border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5">
            <svg width="30" height="30" viewBox="0 0 34 34" fill="none" aria-hidden="true">
              <rect width="34" height="34" rx="8" fill="rgba(255,255,255,0.12)"/>
              <path d="M17 6C17 6 10 12 10 18.5C10 22.09 13.13 25 17 25C20.87 25 24 22.09 24 18.5C24 12 17 6 17 6Z" fill="#F47B20"/>
              <path d="M17 14C17 14 14 17 14 19.5C14 21.16 15.34 22.5 17 22.5C18.66 22.5 20 21.16 20 19.5C20 17 17 14 17 14Z" fill="#FFB343"/>
            </svg>
            <div>
              <p className="font-heading text-base font-semibold text-white leading-tight">M-CareLink</p>
              <p className="text-[9px] uppercase tracking-[0.15em] text-primary-foreground/45">Administration</p>
            </div>
          </div>
        </div>

        {/* Nav Groups */}
        <nav className="flex-1 overflow-y-auto py-3 px-2 space-y-0.5" style={{ scrollbarWidth: 'thin', scrollbarColor: 'rgba(255,255,255,0.1) transparent' }}>
          {sideGroups.map(group => (
            <div key={group.label} className="mb-1">
              <p className="text-[9px] font-semibold tracking-[0.18em] text-primary-foreground/35 px-3 py-2 mt-1">{group.label}</p>
              {group.items.map(({ key, icon: Icon, label }) => (
                <button key={key} onClick={() => setTab(key)}
                  className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                    tab === key
                      ? 'bg-accent/20 text-white border border-accent/20'
                      : 'text-primary-foreground/60 hover:bg-white/8 hover:text-white'
                  }`} data-testid={`admin-tab-${key}`}>
                  <Icon className={`w-4 h-4 flex-shrink-0 ${tab === key ? 'text-accent' : ''}`} />
                  <span className="truncate">{label}</span>
                </button>
              ))}
            </div>
          ))}

          {/* Modules section */}
          <div className="mt-3 pt-3 border-t border-white/10">
            <p className="text-[9px] font-semibold tracking-[0.18em] text-primary-foreground/35 px-3 py-2">MODULES</p>
            {modulesItems.map(({ key, icon: Icon, label }) => (
              <button key={key} onClick={() => setTab(key)}
                className={`w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-[13px] font-medium transition-all ${
                  tab === key
                    ? 'bg-accent/20 text-white border border-accent/20'
                    : 'text-primary-foreground/60 hover:bg-white/8 hover:text-white'
                }`} data-testid={`admin-tab-${key}`}>
                <Icon className={`w-4 h-4 flex-shrink-0 ${tab === key ? 'text-accent' : ''}`} />
                <span className="truncate">{label}</span>
              </button>
            ))}
          </div>
        </nav>

        {/* User footer */}
        <div className="p-3 border-t border-white/10 flex-shrink-0">
          <div className="flex items-center gap-2.5 px-3 py-2 mb-1 rounded-lg bg-white/5">
            <div className="w-8 h-8 rounded-full bg-accent flex items-center justify-center text-sm font-bold text-white flex-shrink-0">
              {user?.first_name?.[0]}{user?.last_name?.[0]}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-xs font-medium truncate text-white">{user?.first_name} {user?.last_name}</p>
              <p className="text-[10px] text-primary-foreground/45 capitalize">{user?.role?.replace('_', ' ')}</p>
            </div>
          </div>
          <button onClick={() => navigate('/')} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-primary-foreground/55 hover:bg-white/8 hover:text-white transition-colors" data-testid="admin-back-site">
            <Home className="w-3.5 h-3.5" /> Retour au site
          </button>
          {/* CDC PDF download */}
          <a
            href="/api/download/cdc-pdf"
            download="MCareLink_Cahier_des_Charges.pdf"
            data-testid="admin-download-cdc"
            className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-secondary/80 hover:bg-secondary/10 hover:text-secondary transition-colors"
          >
            <Download className="w-3.5 h-3.5" /> Cahier des charges PDF
          </a>
          <button onClick={logout} className="w-full flex items-center gap-2.5 px-3 py-2 rounded-lg text-xs text-destructive/80 hover:bg-destructive/10 hover:text-destructive transition-colors" data-testid="admin-logout">
            <LogOut className="w-3.5 h-3.5" /> Déconnexion
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 md:ml-64 p-4 md:p-8">
        {/* Mobile Nav */}
        <div className="md:hidden mb-6">
          <select value={tab} onChange={e => setTab(e.target.value)} className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="admin-mobile-nav">
            {sideItems.map(({ key, label }) => <option key={key} value={key}>{label}</option>)}
          </select>
        </div>

        {/* ============================================================ */}
        {/* OVERVIEW */}
        {/* ============================================================ */}
        {tab === 'overview' && (
          <div data-testid="admin-overview">
            <h1 className="font-heading text-2xl md:text-3xl font-bold mb-6">Tableau de bord</h1>
            {!stats ? (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
                {[1,2,3,4].map(i => (
                  <div key={i} className="bg-white rounded-xl p-5 border border-border/50 animate-pulse">
                    <div className="w-10 h-10 bg-muted rounded-lg mb-3" />
                    <div className="h-7 bg-muted rounded w-16 mb-2" />
                    <div className="h-3 bg-muted rounded w-24" />
                  </div>
                ))}
              </div>
            ) : (
              <>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8">
              {[
                { label: 'Demandes total', value: stats.stay_requests.total, icon: FileText, color: 'primary' },
                { label: 'Nouvelles', value: stats.stay_requests.new, icon: Clock, color: 'accent' },
                { label: 'Partenaires actifs', value: stats.partners.active, icon: Handshake, color: 'secondary' },
                { label: 'Leads total', value: stats.leads.total, icon: TrendingUp, color: 'primary' },
              ].map(({ label, value, icon: Icon, color }) => (
                <div key={label} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center mb-3 ${
                    color === 'accent' ? 'bg-accent/10' : color === 'secondary' ? 'bg-secondary/10' : 'bg-primary/10'
                  }`}>
                    <Icon className={`w-5 h-5 ${color === 'accent' ? 'text-accent' : color === 'secondary' ? 'text-secondary' : 'text-primary'}`} />
                  </div>
                  <p className="font-heading text-2xl font-bold">{value ?? 0}</p>
                  <p className="text-xs text-muted-foreground mt-1">{label}</p>
                </div>
              ))}
            </div>

            {/* Charts Row */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-6">
              {/* Pipeline Bar Chart */}
              <div className="lg:col-span-2 bg-white rounded-xl p-6 border border-border/50 shadow-sm" data-testid="pipeline-chart">
                <h3 className="font-heading text-base font-semibold mb-4">Pipeline des demandes</h3>
                <ResponsiveContainer width="100%" height={200}>
                  <BarChart data={pipelineData} margin={{ top: 0, right: 0, left: -20, bottom: 0 }}>
                    <XAxis dataKey="name" tick={{ fontSize: 11 }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fontSize: 11 }} axisLine={false} tickLine={false} allowDecimals={false} />
                    <Tooltip
                      formatter={(value, name, props) => [value, props.payload.full]}
                      contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #e5e7eb' }}
                    />
                    <Bar dataKey="value" radius={[4, 4, 0, 0]}>
                      {pipelineData.map((entry, index) => (
                        <Cell key={index} fill={CHART_COLORS[index % CHART_COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>

              {/* Partners by category - Pie */}
              <div className="bg-white rounded-xl p-6 border border-border/50 shadow-sm" data-testid="partners-chart">
                <h3 className="font-heading text-base font-semibold mb-4">Partenaires par catégorie</h3>
                {partnerCatData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={200}>
                    <PieChart>
                      <Pie data={partnerCatData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={70} innerRadius={35} label={({ name, value }) => `${value}`}>
                        {partnerCatData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #e5e7eb' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground text-center py-8">Aucun partenaire</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {partnerCatData.map((d, i) => (
                    <span key={d.name} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="w-2 h-2 rounded-full inline-block" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {d.name}
                    </span>
                  ))}
                </div>
              </div>
            </div>

            {/* Leads & Secondary Stats */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Leads by status Pie */}
              <div className="bg-white rounded-xl p-6 border border-border/50 shadow-sm" data-testid="leads-chart">
                <h3 className="font-heading text-base font-semibold mb-4">Leads par statut</h3>
                {leadsStatusData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={150}>
                    <PieChart>
                      <Pie data={leadsStatusData} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={60} innerRadius={30}>
                        {leadsStatusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i % CHART_COLORS.length]} />)}
                      </Pie>
                      <Tooltip contentStyle={{ borderRadius: '8px', fontSize: '12px', border: '1px solid #e5e7eb' }} />
                    </PieChart>
                  </ResponsiveContainer>
                ) : <p className="text-sm text-muted-foreground text-center py-6">Aucun lead</p>}
                <div className="flex flex-wrap gap-2 mt-2">
                  {leadsStatusData.map((d, i) => (
                    <span key={d.name} className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                      <span className="w-2 h-2 rounded-full" style={{ background: CHART_COLORS[i % CHART_COLORS.length] }} />
                      {d.name} ({d.value})
                    </span>
                  ))}
                </div>
              </div>

              {/* Additional stats */}
              <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-3 gap-4">
                <div className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Partenaires en attente</p>
                  <p className="font-heading text-2xl font-bold">{stats.partners.pending}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Demandes B2B</p>
                  <p className="font-heading text-2xl font-bold">{stats.b2b_requests.total}</p>
                </div>
                <div className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <p className="text-sm font-medium text-muted-foreground mb-1">Messages contacts</p>
                  <p className="font-heading text-2xl font-bold">{stats.contacts.total}</p>
                </div>
              </div>
            </div>
              </>
            )}
          </div>
        )}

        {/* ============================================================ */}
        {/* STAY REQUESTS */}
        {/* ============================================================ */}
        {tab === 'stays' && (
          <div data-testid="admin-stays">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Demandes de séjour</h1>
              <Button variant="outline" size="sm" onClick={() => exportCSV(stayRequests, CSV_CONFIGS.stays.filename, CSV_CONFIGS.stays.columns)} className="rounded-full" data-testid="export-stays-btn">
                <Download className="w-4 h-4 mr-2" /> Exporter CSV
              </Button>
            </div>
            <div className="space-y-3">
              {stayRequests.map(req => {
                const assignedPartner = getAssignedPartner(req);
                return (
                  <div key={req.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-3 mb-2 flex-wrap">
                          <h3 className="font-medium text-foreground">{req.first_name} {req.last_name}</h3>
                          <Badge className={`text-xs ${STATUS_COLORS[req.status]}`} data-testid={`stay-badge-${req.id}`}>
                            {STATUS_LABELS[req.status] || req.status}
                          </Badge>
                          {assignedPartner && (
                            <Badge className="text-xs bg-teal-100 text-teal-700 flex items-center gap-1">
                              <Handshake className="w-3 h-3" /> {assignedPartner.partner_company}
                            </Badge>
                          )}
                        </div>
                        <div className="text-sm text-muted-foreground space-y-0.5">
                          <p>{req.email} {req.phone && `| ${req.phone}`}</p>
                          {req.location_preference && <p>Destination : {req.location_preference}</p>}
                          {req.preferred_period && <p>Période : {req.preferred_period}</p>}
                        </div>
                      </div>
                      <div className="flex items-center gap-2 flex-shrink-0">
                        <select value={req.status} onChange={e => updateStatus('stay-requests', req.id, e.target.value)}
                          className="text-xs h-8 px-2 rounded-lg border border-input bg-white" data-testid={`stay-status-select-${req.id}`}>
                          {STAY_STATUSES.map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                        </select>
                        <Dialog>
                          <DialogTrigger asChild>
                            <Button variant="ghost" size="sm" onClick={() => loadMatches(req.id)} data-testid={`stay-detail-${req.id}`}>
                              <Eye className="w-4 h-4" />
                            </Button>
                          </DialogTrigger>
                          <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto">
                            <DialogHeader>
                              <DialogTitle className="font-heading">Demande de {req.first_name} {req.last_name}</DialogTitle>
                            </DialogHeader>
                            <Tabs defaultValue="details">
                              <TabsList className="w-full grid grid-cols-3 mb-4">
                                <TabsTrigger value="details">Détails</TabsTrigger>
                                <TabsTrigger value="matching" data-testid="matching-tab">
                                  <Zap className="w-3.5 h-3.5 mr-1.5" /> Matching
                                </TabsTrigger>
                                <TabsTrigger value="notes">Notes ({req.notes?.filter(n => n.type !== 'partner_match').length || 0})</TabsTrigger>
                              </TabsList>

                              {/* Details tab */}
                              <TabsContent value="details" className="space-y-2 text-sm">
                                <p><strong>Email :</strong> {req.email}</p>
                                <p><strong>Téléphone :</strong> {req.phone || '—'}</p>
                                <p><strong>Destination :</strong> {req.location_preference || '—'}</p>
                                <p><strong>Période :</strong> {req.preferred_period || '—'}</p>
                                <p><strong>Situation :</strong> {req.general_situation || '—'}</p>
                                <p><strong>Mobilité :</strong> {req.mobility_needs || '—'}</p>
                                <p><strong>Assistance :</strong> {req.assistance_needs || '—'}</p>
                                <p><strong>Coordination :</strong> {req.coordination_needs || '—'}</p>
                                <p><strong>Budget :</strong> {req.indicative_budget || '—'}</p>
                                <p><strong>Message :</strong> {req.message || '—'}</p>
                                <p><strong>Rappel :</strong> {req.callback_requested ? 'Oui' : 'Non'}</p>
                                <p><strong>Créé le :</strong> {new Date(req.created_at).toLocaleDateString('fr-FR')}</p>
                                {assignedPartner && (
                                  <div className="mt-3 p-3 rounded-lg bg-teal-50 border border-teal-200">
                                    <p className="text-sm font-semibold text-teal-800 flex items-center gap-2">
                                      <Handshake className="w-4 h-4" /> Partenaire assigné : {assignedPartner.partner_company}
                                    </p>
                                    <p className="text-xs text-teal-600 mt-1">par {assignedPartner.author_name} le {new Date(assignedPartner.created_at).toLocaleDateString('fr-FR')}</p>
                                  </div>
                                )}
                              </TabsContent>

                              {/* Matching tab */}
                              <TabsContent value="matching" data-testid="matching-section">
                                <div className="mb-3">
                                  <p className="text-sm text-muted-foreground mb-3">Partenaires actifs classés par compatibilité avec cette demande.</p>
                                  {matchLoading[req.id] ? (
                                    <div className="flex items-center justify-center py-8">
                                      <div className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                                    </div>
                                  ) : matchData[req.id] ? (
                                    <div className="space-y-2">
                                      {matchData[req.id].length === 0 ? (
                                        <p className="text-center text-muted-foreground py-6 text-sm">Aucun partenaire actif trouvé</p>
                                      ) : matchData[req.id].map(p => (
                                        <div key={p.id} className="flex items-center gap-3 p-3 rounded-xl border border-border/50 hover:bg-muted/30 transition-colors" data-testid={`match-partner-${p.id}`}>
                                          <div className="flex-1 min-w-0">
                                            <div className="flex items-center gap-2 mb-1">
                                              <span className="font-medium text-sm">{p.company_name}</span>
                                              <Badge variant="outline" className="text-xs">{CAT_LABELS[p.category] || p.category}</Badge>
                                            </div>
                                            <p className="text-xs text-muted-foreground">{p.covered_zone} · {p.accessibility_level}</p>
                                            {p.quality_score && (
                                              <p className="text-xs text-muted-foreground flex items-center gap-1 mt-0.5">
                                                <Star className="w-3 h-3 text-amber-400 fill-amber-400" /> {p.quality_score}/5
                                              </p>
                                            )}
                                          </div>
                                          <div className="flex items-center gap-2 flex-shrink-0">
                                            <div className="text-center">
                                              <div className={`w-10 h-10 rounded-full flex items-center justify-center text-xs font-bold ${
                                                p.match_score >= 60 ? 'bg-green-100 text-green-700' :
                                                p.match_score >= 30 ? 'bg-yellow-100 text-yellow-700' :
                                                'bg-gray-100 text-gray-600'
                                              }`}>
                                                {p.match_score}
                                              </div>
                                              <p className="text-[10px] text-muted-foreground mt-0.5">score</p>
                                            </div>
                                            <Button size="sm" variant="outline" className="rounded-full text-xs h-8"
                                              onClick={() => assignPartner(req.id, p)} data-testid={`assign-partner-${p.id}`}>
                                              Assigner
                                            </Button>
                                          </div>
                                        </div>
                                      ))}
                                    </div>
                                  ) : (
                                    <Button onClick={() => loadMatches(req.id)} className="w-full rounded-full" variant="outline">
                                      <Zap className="w-4 h-4 mr-2" /> Calculer les correspondances
                                    </Button>
                                  )}
                                </div>
                              </TabsContent>

                              {/* Notes tab */}
                              <TabsContent value="notes">
                                <div className="space-y-2 mb-4">
                                  {req.notes?.filter(n => n.type !== 'partner_match').map(n => (
                                    <div key={n.id} className="bg-muted/40 rounded-lg p-3 text-xs">
                                      <p>{n.content}</p>
                                      <p className="text-muted-foreground mt-1">{n.author_name} — {new Date(n.created_at).toLocaleDateString('fr-FR')}</p>
                                    </div>
                                  ))}
                                  {(!req.notes || req.notes.filter(n => n.type !== 'partner_match').length === 0) &&
                                    <p className="text-sm text-muted-foreground text-center py-4">Aucune note</p>}
                                </div>
                                <div className="flex gap-2">
                                  <Input value={noteText} onChange={e => setNoteText(e.target.value)} placeholder="Ajouter une note..." className="rounded-lg text-sm" />
                                  <Button size="sm" onClick={() => addNote('stay-requests', req.id)} data-testid="add-note-btn">Ajouter</Button>
                                </div>
                              </TabsContent>
                            </Tabs>
                          </DialogContent>
                        </Dialog>
                      </div>
                    </div>
                  </div>
                );
              })}
              {stayRequests.length === 0 && <p className="text-center text-muted-foreground py-8">Aucune demande de séjour</p>}
            </div>
          </div>
        )}

        {/* PLANNING */}
        {tab === 'planning' && (
          <div data-testid="admin-planning">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Planning & Réservations</h1>
              {bookingsReady && (
                <Button onClick={() => setShowCreateBooking(true)} className="rounded-full" data-testid="create-booking-btn">
                  <Plus className="w-4 h-4 mr-2" /> Nouvelle réservation
                </Button>
              )}
            </div>

            {bookingsReady === false && (
              <div className="bg-amber-50 border border-amber-200 rounded-xl p-6 mb-6">
                <h3 className="font-medium text-amber-800 mb-2">Migration requise</h3>
                <p className="text-sm text-amber-700 mb-3">La table <code className="bg-amber-100 px-1 rounded">bookings</code> n'existe pas encore dans Supabase.</p>
                <p className="text-sm text-amber-700 mb-4">Exécutez ce script SQL dans <strong>Supabase Dashboard → SQL Editor</strong> :</p>
                <pre className="bg-amber-100 rounded-lg p-4 text-xs font-mono overflow-auto text-amber-900 whitespace-pre-wrap">
{`CREATE TABLE IF NOT EXISTS bookings (
    id UUID PRIMARY KEY DEFAULT gen_random_uuid(),
    stay_request_id UUID,
    partner_id UUID,
    caregiver_user_id UUID,
    caregiver_name TEXT DEFAULT '',
    partner_name TEXT DEFAULT '',
    start_date TEXT NOT NULL,
    end_date TEXT NOT NULL,
    status TEXT DEFAULT 'pending',
    notes TEXT DEFAULT '',
    created_by UUID,
    created_at TIMESTAMPTZ DEFAULT NOW(),
    updated_at TIMESTAMPTZ DEFAULT NOW()
);
ALTER TABLE bookings ENABLE ROW LEVEL SECURITY;`}
                </pre>
                <Button onClick={loadBookings} variant="outline" size="sm" className="mt-4 rounded-full">
                  Vérifier à nouveau
                </Button>
              </div>
            )}

            {showCreateBooking && bookingsReady && (
              <div className="bg-white rounded-2xl p-6 border border-border/50 shadow-sm mb-6" data-testid="create-booking-form">
                <div className="flex items-center justify-between mb-4">
                  <h2 className="font-heading text-lg font-semibold">Créer une réservation</h2>
                  <button onClick={() => setShowCreateBooking(false)} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4" /></button>
                </div>
                <form onSubmit={createBooking} className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-sm font-medium block mb-1">Partenaire *</label>
                    <select value={bookingForm.partner_id} onChange={e => setBookingForm(p => ({ ...p, partner_id: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" required data-testid="booking-partner-select">
                      <option value="">Sélectionner un partenaire...</option>
                      {partners.filter(p => p.status === 'active').map(p => (
                        <option key={p.id} value={p.id}>{p.company_name} — {p.covered_zone}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Demande de séjour (optionnel)</label>
                    <select value={bookingForm.stay_request_id} onChange={e => setBookingForm(p => ({ ...p, stay_request_id: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" data-testid="booking-request-select">
                      <option value="">Sans demande liée</option>
                      {stayRequests.filter(r => ['confirmed', 'proposal_sent', 'coordinating'].includes(r.status)).map(r => (
                        <option key={r.id} value={r.id}>{r.first_name} {r.last_name} — {r.location_preference}</option>
                      ))}
                    </select>
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Date de début *</label>
                    <input type="date" value={bookingForm.start_date} onChange={e => setBookingForm(p => ({ ...p, start_date: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" required data-testid="booking-start-date" />
                  </div>
                  <div>
                    <label className="text-sm font-medium block mb-1">Date de fin *</label>
                    <input type="date" value={bookingForm.end_date} onChange={e => setBookingForm(p => ({ ...p, end_date: e.target.value }))}
                      className="w-full h-10 px-3 rounded-xl border border-input bg-white text-sm" required data-testid="booking-end-date" />
                  </div>
                  <div className="md:col-span-2">
                    <label className="text-sm font-medium block mb-1">Notes</label>
                    <textarea value={bookingForm.notes} onChange={e => setBookingForm(p => ({ ...p, notes: e.target.value }))}
                      className="w-full px-3 py-2 rounded-xl border border-input bg-white text-sm min-h-[60px]" placeholder="Instructions particulières..." data-testid="booking-notes" />
                  </div>
                  <div className="md:col-span-2 flex gap-3">
                    <Button type="submit" className="rounded-full" data-testid="booking-submit-btn">Créer la réservation</Button>
                    <Button type="button" variant="outline" onClick={() => setShowCreateBooking(false)} className="rounded-full">Annuler</Button>
                  </div>
                </form>
              </div>
            )}

            {bookingsReady && (
              <>
              <div className="space-y-3" data-testid="bookings-list">
                <h2 className="font-heading text-lg font-semibold mt-2 mb-3">Réservations</h2>
                {bookings.length === 0 ? (
                  <div className="bg-white rounded-xl p-10 text-center border border-border/50 text-muted-foreground">
                    <CalendarDays className="w-10 h-10 mx-auto mb-3 opacity-20" />
                    <p>Aucune réservation. Créez la première depuis le bouton ci-dessus.</p>
                  </div>
                ) : bookings.map(b => {
                  const bookingStatusColors = {
                    pending: 'bg-orange-100 text-orange-700', confirmed: 'bg-blue-100 text-blue-700',
                    in_progress: 'bg-teal-100 text-teal-700', completed: 'bg-green-100 text-green-700',
                    cancelled: 'bg-gray-100 text-gray-500'
                  };
                  const bookingStatusLabels = {
                    pending: 'En attente', confirmed: 'Confirmée',
                    in_progress: 'En cours', completed: 'Terminée', cancelled: 'Annulée'
                  };
                  return (
                    <div key={b.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm" data-testid={`booking-item-${b.id}`}>
                      <div className="flex items-start justify-between gap-4">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2 flex-wrap">
                            <h3 className="font-medium">{b.partner_name}</h3>
                            <Badge className={`text-xs ${bookingStatusColors[b.status]}`}>{bookingStatusLabels[b.status]}</Badge>
                          </div>
                          <div className="text-sm text-muted-foreground space-y-0.5">
                            {b.caregiver_name && <p>Aidant(e) : {b.caregiver_name}</p>}
                            <p className="flex items-center gap-2">
                              <CalendarDays className="w-3.5 h-3.5" />
                              {new Date(b.start_date).toLocaleDateString('fr-FR')} → {new Date(b.end_date).toLocaleDateString('fr-FR')}
                            </p>
                            {b.notes && <p className="text-xs italic mt-1">{b.notes}</p>}
                          </div>
                        </div>
                        <div className="flex-shrink-0">
                          {b.status !== 'cancelled' && b.status !== 'completed' && (
                            <select value={b.status} onChange={e => updateBookingStatus(b.id, e.target.value)}
                              className="text-xs h-8 px-2 rounded-lg border border-input bg-white" data-testid={`booking-status-${b.id}`}>
                              {['pending', 'confirmed', 'in_progress', 'completed', 'cancelled'].map(s => (
                                <option key={s} value={s}>{{ pending: 'En attente', confirmed: 'Confirmée', in_progress: 'En cours', completed: 'Terminée', cancelled: 'Annulée' }[s]}</option>
                              ))}
                            </select>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
              </div>

              {/* Partner Slots overview */}
              {allSlots.length > 0 && (
                <div className="mt-8" data-testid="admin-slots-list">
                  <h2 className="font-heading text-lg font-semibold mb-3">Créneaux partenaires disponibles</h2>
                  <div className="space-y-3">
                    {allSlots.map(slot => (
                      <div key={slot.id} className="bg-white rounded-xl p-4 border border-border/50 shadow-sm flex items-center gap-4" data-testid={`admin-slot-${slot.id}`}>
                        <div className="w-9 h-9 rounded-xl bg-secondary/10 flex items-center justify-center flex-shrink-0">
                          <CalendarDays className="w-4 h-4 text-secondary" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <span className="font-medium text-sm">{slot.title}</span>
                            <Badge variant="outline" className="text-xs">{slot.partner_name}</Badge>
                            {slot.price_range && <span className="text-xs text-muted-foreground">{slot.price_range}</span>}
                          </div>
                          <p className="text-xs text-muted-foreground mt-0.5">
                            {new Date(slot.start_date).toLocaleDateString('fr-FR')} → {new Date(slot.end_date).toLocaleDateString('fr-FR')}
                            &nbsp;·&nbsp; {slot.booked_count}/{slot.capacity} places
                          </p>
                        </div>
                        <Badge className="text-xs bg-green-100 text-green-700 flex-shrink-0">Disponible</Badge>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              </>
            )}
          </div>
        )}

        {/* PARTNERS */}
        {tab === 'partners' && (
          <div data-testid="admin-partners">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Partenaires</h1>
              <Button variant="outline" size="sm" onClick={() => exportCSV(partners, CSV_CONFIGS.partners.filename, CSV_CONFIGS.partners.columns)} className="rounded-full" data-testid="export-partners-btn">
                <Download className="w-4 h-4 mr-2" /> Exporter CSV
              </Button>
            </div>
            <div className="space-y-3">
              {partners.map(p => (
                <div key={p.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2 flex-wrap">
                        <h3 className="font-medium">{p.company_name}</h3>
                        <Badge className={`text-xs ${STATUS_COLORS[p.status]}`}>{STATUS_LABELS[p.status]}</Badge>
                        <Badge variant="outline" className="text-xs">{CAT_LABELS[p.category] || p.category}</Badge>
                        {p.quality_score && (
                          <span className="text-xs text-amber-600 flex items-center gap-1">
                            <Star className="w-3 h-3 fill-amber-400 text-amber-400" /> {p.quality_score}/5
                          </span>
                        )}
                      </div>
                      <p className="text-sm text-muted-foreground">{p.contact_first_name} {p.contact_last_name} — {p.contact_email}</p>
                      <p className="text-sm text-muted-foreground">Zone : {p.covered_zone} | Accessibilité : {p.accessibility_level}</p>
                    </div>
                    <select value={p.status} onChange={e => updateStatus('partners', p.id, e.target.value)}
                      className="text-xs h-8 px-2 rounded-lg border border-input bg-white flex-shrink-0" data-testid={`partner-status-${p.id}`}>
                      {['pending', 'active', 'rejected'].map(s => <option key={s} value={s}>{STATUS_LABELS[s]}</option>)}
                    </select>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* B2B */}
        {tab === 'b2b' && (
          <div data-testid="admin-b2b">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Demandes B2B</h1>
              <Button variant="outline" size="sm" onClick={() => exportCSV(b2bRequests, CSV_CONFIGS.b2b.filename, CSV_CONFIGS.b2b.columns)} className="rounded-full" data-testid="export-b2b-btn">
                <Download className="w-4 h-4 mr-2" /> Exporter CSV
              </Button>
            </div>
            <div className="space-y-3">
              {b2bRequests.map(b => (
                <div key={b.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium">{b.organization}</h3>
                    <Badge className={`text-xs ${STATUS_COLORS[b.status]}`}>{STATUS_LABELS[b.status]}</Badge>
                    <Badge variant="outline" className="text-xs">{b.actor_type}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{b.contact_first_name} {b.contact_last_name} — {b.contact_email}</p>
                  <p className="text-sm text-muted-foreground">{b.needs}</p>
                  <p className="text-xs text-muted-foreground mt-1">Volume : {b.estimated_volume} | Zone : {b.zone}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* CONTACTS */}
        {tab === 'contacts' && (
          <div data-testid="admin-contacts">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Messages de contact</h1>
              <Button variant="outline" size="sm" onClick={() => exportCSV(contacts, CSV_CONFIGS.contacts.filename, CSV_CONFIGS.contacts.columns)} className="rounded-full" data-testid="export-contacts-btn">
                <Download className="w-4 h-4 mr-2" /> Exporter CSV
              </Button>
            </div>
            <div className="space-y-3">
              {contacts.map(c => (
                <div key={c.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="font-medium">{c.name}</h3>
                    <Badge className={`text-xs ${STATUS_COLORS[c.status]}`}>{STATUS_LABELS[c.status] || c.status}</Badge>
                  </div>
                  <p className="text-sm text-muted-foreground">{c.email} {c.phone && `| ${c.phone}`}</p>
                  {c.subject && <p className="text-sm text-muted-foreground">Sujet : {c.subject}</p>}
                  <p className="text-sm mt-2">{c.message}</p>
                  <p className="text-xs text-muted-foreground mt-2">{new Date(c.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* LEADS */}
        {tab === 'leads' && (
          <div data-testid="admin-leads">
            <div className="flex items-center justify-between mb-6">
              <h1 className="font-heading text-2xl font-bold">Leads</h1>
              <Button variant="outline" size="sm" onClick={() => exportCSV(leads, CSV_CONFIGS.leads.filename, CSV_CONFIGS.leads.columns)} className="rounded-full" data-testid="export-leads-btn">
                <Download className="w-4 h-4 mr-2" /> Exporter CSV
              </Button>
            </div>
            <div className="space-y-3">
              {leads.map(l => (
                <div key={l.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm flex items-center gap-4">
                  <div className="flex-1">
                    <div className="flex items-center gap-3 mb-1">
                      <h3 className="font-medium text-sm">{l.name}</h3>
                      <Badge className={`text-xs ${STATUS_COLORS[l.status]}`}>{STATUS_LABELS[l.status] || l.status}</Badge>
                      <Badge variant="outline" className="text-xs">{l.type}</Badge>
                    </div>
                    <p className="text-xs text-muted-foreground">{l.email} | Source : {l.source}</p>
                  </div>
                  <p className="text-xs text-muted-foreground">{new Date(l.created_at).toLocaleDateString('fr-FR')}</p>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* FAQ */}
        {tab === 'faq' && (
          <div data-testid="admin-faq">
            <h1 className="font-heading text-2xl font-bold mb-6">Gestion FAQ</h1>
            <div className="bg-white rounded-xl p-6 border border-border/50 shadow-sm mb-6">
              <h3 className="font-medium mb-4">{editingFaq ? 'Modifier' : 'Ajouter'} une question</h3>
              <div className="space-y-3">
                <Input value={faqForm.question} onChange={e => setFaqForm(p => ({ ...p, question: e.target.value }))} placeholder="Question" className="rounded-xl" data-testid="faq-question-input" />
                <Textarea value={faqForm.answer} onChange={e => setFaqForm(p => ({ ...p, answer: e.target.value }))} placeholder="Réponse" className="rounded-xl min-h-[80px]" data-testid="faq-answer-input" />
                <div className="flex gap-3 flex-wrap">
                  <Input value={faqForm.category} onChange={e => setFaqForm(p => ({ ...p, category: e.target.value }))} placeholder="Catégorie" className="rounded-xl flex-1" />
                  <Input type="number" value={faqForm.order} onChange={e => setFaqForm(p => ({ ...p, order: parseInt(e.target.value) || 0 }))} placeholder="Ordre" className="rounded-xl w-24" />
                  <Button onClick={saveFaq} className="rounded-full" data-testid="faq-save-btn">{editingFaq ? 'Modifier' : 'Ajouter'}</Button>
                  {editingFaq && <Button variant="outline" onClick={() => { setEditingFaq(null); setFaqForm({ question: '', answer: '', category: 'general', order: 0 }); }} className="rounded-full">Annuler</Button>}
                </div>
              </div>
            </div>
            <div className="space-y-3">
              {faqItems.map(f => (
                <div key={f.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm">
                  <div className="flex items-start justify-between gap-4">
                    <div className="flex-1">
                      <h3 className="font-medium text-sm">{f.question}</h3>
                      <p className="text-xs text-muted-foreground mt-1">{f.answer.substring(0, 120)}...</p>
                      <div className="flex gap-2 mt-2">
                        <Badge variant="outline" className="text-xs">{f.category}</Badge>
                        <Badge variant={f.is_active ? 'default' : 'secondary'} className="text-xs">{f.is_active ? 'Active' : 'Inactive'}</Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button variant="ghost" size="sm" onClick={() => { setEditingFaq(f.id); setFaqForm({ question: f.question, answer: f.answer, category: f.category, order: f.order }); }}>
                        <Edit className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="sm" onClick={() => deleteFaq(f.id)}>
                        <XCircle className="w-4 h-4 text-destructive" />
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* USERS */}
        {tab === 'users' && user?.role === 'super_admin' && (
          <div data-testid="admin-users">
            <h1 className="font-heading text-2xl font-bold mb-6">Utilisateurs</h1>
            <div className="space-y-3">
              {users.map(u => (
                <div key={u.id} className="bg-white rounded-xl p-5 border border-border/50 shadow-sm flex items-center gap-4">
                  <div className="w-10 h-10 rounded-full bg-primary/10 flex items-center justify-center text-sm font-bold text-primary">
                    {u.first_name?.[0]}{u.last_name?.[0]}
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-medium text-sm">{u.first_name} {u.last_name}</p>
                    <p className="text-xs text-muted-foreground">{u.email}</p>
                  </div>
                  <Badge className={`text-xs ${u.role === 'super_admin' ? 'bg-purple-100 text-purple-700' : STATUS_COLORS[u.status] || ''}`}>{u.role}</Badge>
                  <Badge className={`text-xs ${u.status === 'active' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-600'}`}>{u.status}</Badge>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* ── ACTIVE MODULES ── */}
        {tab === 'analytics' && <AnalyticsModule />}
        {tab === 'catalog' && <CatalogModule />}
        {tab === 'surveys' && <SurveysModule />}
        {tab === 'community' && <CommunityModule />}
        {tab === 'cms' && <CMSModule />}
        {tab === 'notifications' && <NotificationsModule />}
        {tab === 'crm' && <CRMModule />}
        {tab === 'settings' && <SettingsModule />}

        {/* ── IA MATCHING placeholder (accès via Demandes séjour) ── */}
        {tab === 'matching' && (
          <div data-testid="admin-matching">
            <div className="flex flex-col items-center justify-center min-h-[60vh] text-center max-w-md mx-auto">
              <div className="w-16 h-16 rounded-2xl bg-primary/8 flex items-center justify-center mb-5">
                <Zap className="w-8 h-8 text-primary/60" />
              </div>
              <span className="inline-block text-xs font-medium text-accent bg-accent/10 px-3 py-1 rounded-full mb-4">Intégré au workflow</span>
              <h1 className="font-heading text-2xl font-semibold mb-3">IA Matching</h1>
              <p className="text-sm text-muted-foreground leading-relaxed">
                Le moteur de matching est accessible directement depuis l'onglet <strong>Demandes séjour</strong>.<br />
                Ouvrez une demande et cliquez sur "Voir les partenaires compatibles".
              </p>
              <Button onClick={() => setTab('stays')} className="mt-6 rounded-full bg-accent hover:bg-accent/90 text-white font-body">
                Aller aux demandes séjour
              </Button>
            </div>
          </div>
        )}

      </main>
    </div>
  );
}
